import FinancialFilters from "./FinancialFilters";
import React, { createContext, useCallback, useEffect, useRef, useState } from "react";
import { FormProvider } from "@unform/core";
import { toast } from "react-toastify";
import FindGeneric from "~/components/Finds/FindGeneric";
import Grid from "~/components/Grid";
import Filters from "~/components/Filters";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { Field, Row, Section, fieldClass, buttonClass, errorText } from "./shared";

const GroupInputContext = createContext({ setSuccessfullyUpdated: () => null });
const AudienceGridContext = createContext({});
const headers = [
  { title: "Student", name: "name", type: "text", filter: false },
  { title: "Registration", name: "registration_number", type: "text", filter: false },
  { title: "Student Group", name: "studentgroup_id", type: "text", filter: false },
  { title: "Status", name: "status", type: "text", filter: false },
];

export default function Audience({ value, onChange, filial, readonly, request, financialEnabled = false, trackInvoices = false, onTrackInvoicesChange }) {
  const mode = value.mode || "";
  const [options, setOptions] = useState({ courses: [], substatuses: [] });
  const [optionsError, setOptionsError] = useState("");
  const advanced = Object.fromEntries(["population", "programcategory_id", "processsubstatus_id", "overdue", "enrolled_from", "enrolled_to", "birthday_today", "financial"].filter(key => value[key] !== undefined).map(key => [key, value[key]]));
  const advancedKey = JSON.stringify(advanced);
  useEffect(() => {
    if (financialEnabled && value.mode && !value.financial && !readonly) onChange({ ...value, financial: { student_status: value.population === "alumni" ? "Inactive" : "In Class" } });
  }, [financialEnabled, value, readonly, onChange]);
  const [review, setReview] = useState(!!value.mode);
  const [knownGroups, setKnownGroups] = useState({});
  const [groupStatuses, setGroupStatuses] = useState({});
  const [groupError, setGroupError] = useState("");
  const [browseGroup, setBrowseGroup] = useState("");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(null);
  const [orderBy, setOrderBy] = useState({ column: "name", asc: true });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);
  const [revision, setRevision] = useState(0);
  const timer = useRef(null);
  const selectedIds = value.student_ids || [];
  const criteria = JSON.stringify(value);

  const groupId = mode === "group" ? value.studentgroup_id || "" : browseGroup;
  const complete = mode === "branch" || mode === "group" && !!value.studentgroup_id || mode === "students" && selectedIds.length > 0;
  const previewing = review || mode === "group" && complete;
  const previewCriteria = previewing ? criteria : "";
  const showTable = mode === "students" && !review || previewing && complete;
  const limit = 20;

  useEffect(() => { if (error) toast.error(error); }, [error]);
  useEffect(() => { if (optionsError) toast.error(optionsError); }, [optionsError]);
  useEffect(() => { if (groupError) toast.error(groupError); }, [groupError]);
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    const abort = new AbortController();
    request("get", "/messaging/campaigns/audience/options", undefined, abort.signal)
      .then(({ data }) => { if (!abort.signal.aborted) setOptions(data); })
      .catch(failure => { if (!abort.signal.aborted) setOptionsError(errorText(failure)); });
    return () => abort.abort();
  }, [request]);
  const changeAdvanced = (key, next) => {
    const filters = { ...value };
    if (next === "") delete filters[key]; else filters[key] = next;
    if (mode === "students" && selectedIds.length && !window.confirm("Changing recipient filters will clear the selected students. Continue?")) return;
    if (mode === "students") filters.student_ids = [];
    clearTimeout(timer.current); setSearch(null); setPage(1); setReview(false); setSummary(null);
    onChange(filters);
  };
  const loadGroups = useCallback(async ({ search, limit, signal }) => {
    setGroupError("");
    try {
      const { data } = await request("get", "/messaging/campaigns/audience/groups", undefined, signal, { search, limit: Math.min(limit, 100), page: 1 });
      if (signal.aborted) return [];
      setKnownGroups(current => ({ ...current, ...Object.fromEntries(data.rows.map(group => [group.id, group.name])) }));
      setGroupStatuses(current => ({ ...current, ...Object.fromEntries(data.rows.map(group => [group.id, group.status])) }));
      return data.rows;
    } catch (failure) {
      if (!signal.aborted) setGroupError(errorText(failure));
      return [];
    }
  }, [request]);

  useEffect(() => {
    const abort = new AbortController();
    setRows([]);
    setTotal(0);
    setSummary(null);
    setError("");
    if (!showTable) { setLoading(false); return () => abort.abort(); }
    setLoading(true);
    const params = { page, limit, orderBy: orderBy.column, orderASC: orderBy.asc ? "ASC" : "DESC" };
    const pending = previewing
      ? request("post", "/messaging/campaigns/audience/preview", { audience_filters: JSON.parse(previewCriteria) }, abort.signal, params)
      : request("get", "/messaging/campaigns/audience/students", undefined, abort.signal, { ...params, ...JSON.parse(advancedKey), financial: value.financial ? JSON.stringify(value.financial) : undefined, search: search?.value || "", studentgroup_id: browseGroup || undefined });
    pending.then(({ data }) => {
      if (abort.signal.aborted) return;
      if (previewing && Number(data.filial_id) !== Number(filial.id)) throw new Error("Branch mismatch");
      const maxPage = Math.max(1, Math.ceil(data.totalRows / limit));
      if (Number(page) > maxPage) { setPage(maxPage); return; }
      setRows(data.rows);
      setTotal(data.totalRows);
      if (previewing) setSummary({ ...data.summary, criteria: previewCriteria, generated_at: data.generated_at });
      if (data.group) setKnownGroups(current => ({ ...current, [data.group.id]: data.group.name }));
    }).catch(failure => { if (!abort.signal.aborted) setError(errorText(failure)); })
      .finally(() => { if (!abort.signal.aborted) setLoading(false); });
    return () => abort.abort();
  }, [mode, showTable, previewing, previewCriteria, page, orderBy, search, browseGroup, request, filial.id, revision, advancedKey]);

  const handleFilters = ({ title, value: term }) => {
    if (title !== "search") return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { setPage(1); setSearch({ value: term }); }, 500);
  };
  const changeMode = (next) => {
    if (next !== mode && (selectedIds.length || value.studentgroup_id) && !window.confirm("Changing the selection method will clear the current recipients. Continue?")) return;
    clearTimeout(timer.current); setPage(1); setSearch(null); setBrowseGroup(""); setReview(false); setSummary(null);
    onChange(next === "branch" ? { ...advanced, mode: next } : next === "group" ? { ...advanced, mode: next, studentgroup_id: null } : next === "students" ? { ...advanced, mode: next, student_ids: [] } : {});
  };
  const selection = mode === "students" && !readonly ? {
    multiple: true, selected: selectedIds.map(id => ({ id })), functions: [],
    setSelected: selected => {
      if (selected.length > 1000) { setError("Select up to 1,000 students individually. Use a location or whole group for larger audiences."); return; }
      if (!selected.length) setReview(false);
      setPage(review ? 1 : page);
      onChange({ ...advanced, mode: "students", student_ids: [...new Set(selected.map(row => row.id))] });
    },
  } : null;
  const gridData = rows.map(row => ({
    id: row.id, show: true, selectable: !readonly, canceled: !row.eligible,
    fields: [[row.name, row.last_name].filter(Boolean).join(" ") || "Unavailable student", row.registration_number || "", row.studentgroup_name || "", row.eligible ? row.status : row.exclusion_reason === "unavailable" ? "Excluded: unavailable in this location" : "Excluded: does not match filters"],
  }));

  const groupFilters = <div className="w-full" aria-label="Student group filter" role="group">
    <FormProvider onSubmit={() => null}>
      <FindGeneric key={mode + ":" + groupId + ":" + (knownGroups[groupId] || "")}
        title="Student Group" scope="audience_studentgroup" InputContext={GroupInputContext}
        defaultValue={groupId ? { id: Number(groupId), name: knownGroups[groupId] || "Selected group #" + groupId, status: groupStatuses[groupId] } : {}}
        fields={[{ title: "Name", name: "name" }, { title: "Status", name: "status" }]}
        readOnly={readonly} clearable clearValue={{}} required={mode === "group"}
        loadOptions={loadGroups} maxLimit={100}
        setReturnFindGeneric={group => {
          if (readonly) return;
          setPage(1);
          if (mode === "group") onChange({ ...advanced, mode: "group", studentgroup_id: group.id ? Number(group.id) : null });
          else { clearTimeout(timer.current); setSearch(null); setBrowseGroup(group.id ? String(group.id) : ""); setReview(false); }
        }} />
    </FormProvider>
    {groupError && <p role="alert" className="text-xs text-red-700">{groupError}</p>}
  </div>;

  return <section aria-label="Campaign audience" className="w-full">
    <Section title="Recipients">
      <Row>
        <Field label="Selection method"><select className={fieldClass} value={mode} disabled={readonly} onChange={event => changeMode(event.target.value)}>
          <option value="">Select</option>
          <option value="branch">All matching students in this location</option>
          <option value="group">Whole Student Group</option>
          <option value="students">Specific students</option>
        </select></Field>
        {mode && !financialEnabled && <Field label="Student status"><select className={fieldClass} disabled={readonly} value={value.population || "active"} onChange={event => changeAdvanced("population", event.target.value)}><option value="active">Students in class</option><option value="alumni">Ex-students (inactive)</option></select></Field>}
      </Row>
      {financialEnabled && <>
        <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={trackInvoices} disabled={readonly} onChange={event => onTrackInvoicesChange?.(event.target.checked)} />Track invoices in this campaign</label>
        <p className="text-xs text-gray-600">When enabled, every invoice matching Financial filters is saved for each recipient and followed after sending, including reminders without payment links. Invoice Rules still chooses one invoice for message variables.</p>
        {!mode && <p className="text-xs text-gray-600">Select the recipient selection method to configure financial filters.</p>}
        <FinancialFilters value={value.financial || {}} paymentMethods={options.paymentMethods || []} readonly={readonly || !mode || !!optionsError}
          onChange={financial => { setPage(1); setReview(true); setSummary(null); onChange({ ...value, financial }); }} />
        {optionsError && <p role="alert" className="text-xs text-red-700">Unable to load financial filter options: {optionsError}</p>}
      </>}
      {mode === "group" && groupFilters}
      {mode && <>
        <Row>
          <Field label="Course"><select className={fieldClass} disabled={readonly} value={value.programcategory_id || ""} onChange={event => changeAdvanced("programcategory_id", event.target.value)}><option value="">All courses</option>{options.courses?.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></Field>
          <Field label="Process / visa substatus"><select className={fieldClass} disabled={readonly} value={value.processsubstatus_id || ""} onChange={event => changeAdvanced("processsubstatus_id", event.target.value ? Number(event.target.value) : "")}><option value="">All substatuses</option>{options.substatuses?.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></Field>
          <Field label="Overdue invoice"><select className={fieldClass} disabled={readonly} value={value.overdue || ""} onChange={event => changeAdvanced("overdue", event.target.value)}><option value="">Any</option><option value="flagged">Flagged</option><option value="not_flagged">Not flagged</option></select></Field>
        </Row>
        <Row>
          <Field label="Enrollment date from"><input type="date" className={fieldClass} disabled={readonly} value={value.enrolled_from || ""} onChange={event => changeAdvanced("enrolled_from", event.target.value)} /></Field>
          <Field label="Enrollment date to"><input type="date" className={fieldClass} disabled={readonly} value={value.enrolled_to || ""} onChange={event => changeAdvanced("enrolled_to", event.target.value)} /></Field>
          {mode === "students" && !review && <div className="flex w-full flex-col md:w-auto md:min-w-72 md:flex-1">{groupFilters}</div>}
        </Row>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" disabled={readonly} checked={value.birthday_today === true} onChange={event => changeAdvanced("birthday_today", event.target.checked ? true : "")} />Only students whose birthday is on the sending date</label>
        {optionsError && <p role="alert" className="text-xs text-red-700">{optionsError}</p>}
      </>}

      {mode === "students" && <div className="flex flex-wrap items-center gap-2">
        <strong className="text-sm">{selectedIds.length} selected students</strong>
        {!readonly && <button type="button" className={buttonClass} onClick={() => { setReview(current => !current && selectedIds.length > 0); setPage(1); }}>{review ? "Add students" : "View selected students"}</button>}
        {!readonly && selectedIds.length > 0 && <button type="button" className={buttonClass} onClick={() => { onChange({ ...advanced, mode: "students", student_ids: [] }); setReview(false); setPage(1); }}>Clear selection</button>}
      </div>}
      {mode && <div className="flex flex-wrap items-center gap-3">
        <button type="button" className={buttonClass} disabled={!complete || loading} onClick={() => { setReview(true); setPage(1); setRevision(current => current + 1); }}>{mode === "group" && complete ? "Refresh group members" : "Review recipients"}</button>
        {previewing && summary?.criteria === criteria && <p role="status" className="text-xs">{summary.total} in selection · {summary.eligible} eligible · {summary.excluded} excluded</p>}
      </div>}
      {error && <p role="alert" className="text-xs text-red-700">{error}</p>}
      {showTable && <AudienceGridContext.Provider value={{ gridData, gridHeader: headers, page, setPage, pages: Math.max(1, Math.ceil(total / limit)), limit, gridDetails: { totalRows: total }, activeFilters: [], search, handleFilters,
        orderBy, setOrderBy: next => { setPage(1); setOrderBy(next); }, opened: null, handleOpened: () => null }}>
        <div className="w-full"><Filters key={browseGroup} Context={AudienceGridContext} access={{}} showSearch={!previewing} /></div>
        <div className="flex h-80 w-full min-w-0 min-h-0 flex-col overflow-hidden">{loading ? <FormLoading /> : <Grid Context={AudienceGridContext} handleEdit={false} selection={selection} />}</div>
      </AudienceGridContext.Provider>}
    </Section>
  </section>;
}
